import { useEffect, useState } from "react";
import axios from "axios";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Trophy, ChevronDown, BadgeCheck, Zap, Handshake } from "lucide-react";
import { useLenis, scrollToSection } from "@/App";
import { HERO_IMG, HERO_BG, RECORDS, combinedOdds } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MaskedLine = ({ children, delay = 0 }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const DEFAULT_HERO_STATS = [
    { id: "hero-stat-singles-win-rate", value: "77.02%", label: "SINGLES WIN RATE" },
    { id: "hero-stat-doubles-win-rate", value: "74.04%", label: "DOUBLES WIN RATE" },
    { id: "hero-stat-total-picks", value: "1,125", label: "TOTAL PICKS LOGGED" },
    { id: "hero-stat-total-wins", value: "851", label: "TOTAL WINS" },
];

export const Hero = () => {
    const lenisRef = useLenis();
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
    const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
    const [latest, setLatest] = useState({
        date: RECORDS[0].date,
        match1: RECORDS[0].matches[0].teams,
        match2: RECORDS[0].matches[1].teams,
        combined: combinedOdds(RECORDS[0]),
        status: "won",
    });
    const [stats, setStats] = useState(DEFAULT_HERO_STATS);
    const [latestSingle, setLatestSingle] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/content`)
            .then((res) => {
                const d = res.data?.stats;
                const s = res.data?.singles_stats;
                if (d && s) {
                    setStats([
                        { id: "hero-stat-singles-win-rate", value: `${s.win_rate}%`, label: "SINGLES WIN RATE" },
                        { id: "hero-stat-doubles-win-rate", value: `${d.win_rate}%`, label: "DOUBLES WIN RATE" },
                        { id: "hero-stat-total-picks", value: (Number(s.total_matches) + Number(d.total_doubles)).toLocaleString(), label: "TOTAL PICKS LOGGED" },
                        { id: "hero-stat-total-wins", value: (Number(s.wins) + Number(d.wins)).toLocaleString(), label: "TOTAL WINS" },
                    ]);
                }
            })
            .catch(() => {});
        axios
            .get(`${API}/doubles`)
            .then((res) => {
                const first = (Array.isArray(res.data) ? res.data : [])[0];
                if (first) {
                    setLatest({
                        date: first.date,
                        match1: first.matches[0].teams,
                        match2: first.matches[1].teams,
                        combined: combinedOdds(first),
                        status: first.status,
                    });
                }
            })
            .catch(() => {});
        axios
            .get(`${API}/singles`)
            .then((res) => {
                const first = (Array.isArray(res.data) ? res.data : []).find((x) => x.status !== "nomatch");
                if (first) setLatestSingle(first);
            })
            .catch(() => {});
    }, []);

    const onMouseMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section id="home" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-[72px]">
            <div className="absolute inset-0">
                <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-[0.14]" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#05070A]/70 via-[#05070A]/85 to-[#05070A]" />
                <div className="absolute inset-0 bn-grid-bg opacity-40 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_30%,black,transparent)]" />
                <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-bn-blue/20 blur-[140px]" />
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-14 items-center w-full">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="flex items-center gap-3 mb-6"
                        data-testid="hero-partnership-badge"
                    >
                        <span className="w-10 h-10 rounded-xl bg-bn-blue/15 border border-bn-blue/40 flex items-center justify-center shadow-[0_0_20px_rgba(0,102,255,0.25)]">
                            <Zap className="w-5 h-5 text-bn-blue" />
                        </span>
                        <Handshake className="w-6 h-6 text-bn-cyan" />
                        <span className="w-10 h-10 rounded-xl bg-white/5 border border-white/15 flex items-center justify-center">
                            <svg viewBox="0 0 27 18" className="w-6 h-4 rounded-[3px] overflow-hidden" role="img" aria-label="Greek flag">
                                <rect width="27" height="18" fill="#0D5EAF" />
                                <g fill="#ffffff">
                                    <rect y="2" width="27" height="2" />
                                    <rect y="6" width="27" height="2" />
                                    <rect y="10" width="27" height="2" />
                                    <rect y="14" width="27" height="2" />
                                </g>
                                <rect width="10" height="10" fill="#0D5EAF" />
                                <rect x="4" width="2" height="10" fill="#ffffff" />
                                <rect y="4" width="10" height="2" fill="#ffffff" />
                            </svg>
                        </span>
                        <span className="ml-1 text-xs sm:text-sm font-medium text-slate-300 tracking-wide">
                            Collaboration Between BettingNator Tipsters (Singles) &amp; Greek Tipsters (Doubles)
                        </span>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-bn-blue/30 bg-bn-blue/10 mb-8"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-bn-cyan animate-pulse" />
                        <span className="font-mono text-[11px] tracking-[0.25em] text-bn-cyan font-semibold">DAILY SINGLES & DOUBLES · DATA-DRIVEN ANALYSIS</span>
                    </motion.div>

                    <h1 data-testid="hero-headline" className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-[64px] text-white">
                        <MaskedLine delay={0.25}>Professional Football</MaskedLine>
                        <MaskedLine delay={0.38}>Betting. <span className="text-bn-blue text-glow-blue">Smarter Daily</span></MaskedLine>
                        <MaskedLine delay={0.51}><span className="text-bn-blue text-glow-blue">Singles &amp; Doubles.</span></MaskedLine>
                    </h1>

                    <motion.p
                        data-testid="hero-subheading"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.8 }}
                        className="mt-7 text-base lg:text-lg text-slate-400 max-w-xl leading-relaxed"
                    >
                        <span className="block text-white font-semibold">BettingNator.info Partners with Greece's Top Tipsters Mr. Nikos and Mr Georgios.</span>
                        <span className="block mt-3">
                            BettingNator.info is now collaborating with two of Greece's most experienced and respected football tipsters, bringing more than 15 years of combined experience to our Daily Double selections. Our experienced BettingNator.info tipsters focus on carefully selected Single Matches, with odds between 1.90 and 2.50, while our two Greek tipsters specialise in Daily Double Matches with combined odds of 2.50 or higher. Every selection is based on detailed analysis of statistics, recent form, team news, tactical approach, performance trends and market movements. This collaboration brings together proven experience and professional football analysis to deliver carefully selected Singles and Doubles every day.
                        </span>
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.95 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-cta-explore-subscriptions"
                            onClick={() => scrollToSection(lenisRef.current, "subscription")}
                            className="group inline-flex items-center gap-2.5 px-7 py-4 rounded-full bg-bn-blue hover:bg-bn-bluehover text-white font-semibold text-sm tracking-wide transition-all duration-300 hover:shadow-[0_0_35px_rgba(0,102,255,0.55)] hover:-translate-y-0.5"
                        >
                            VIEW SUBSCRIPTION PLANS
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <button
                            data-testid="hero-cta-view-winning-doubles"
                            onClick={() => scrollToSection(lenisRef.current, "records")}
                            className="inline-flex items-center gap-2.5 px-7 py-4 rounded-full border border-white/15 hover:border-bn-blue/60 text-slate-200 hover:text-white font-semibold text-sm tracking-wide transition-all duration-300 hover:bg-bn-blue/10"
                        >
                            <Trophy className="w-4 h-4 text-bn-green" />
                            VIEW RECORDS
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.15 }}
                        className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl"
                    >
                        {stats.map((s) => (
                            <div key={s.id} data-testid={s.id} className="border-l border-bn-blue/30 pl-4">
                                <div className="font-display font-extrabold text-2xl lg:text-3xl text-white">{s.value}</div>
                                <div className="font-mono text-[10px] tracking-[0.18em] text-slate-500 mt-1.5">{s.label}</div>
                            </div>
                        ))}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.3 }}
                        className="mt-10 flex flex-col gap-3 max-w-md lg:hidden"
                        data-testid="hero-latest-cards-mobile"
                    >
                        <div className="bg-[#0B0F17]/90 backdrop-blur-xl border border-bn-blue/30 rounded-xl px-4 py-3.5">
                            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-slate-500">
                                <BadgeCheck className="w-3.5 h-3.5 text-bn-green" />
                                LATEST DOUBLE · {latest.date}
                            </div>
                            <div className="mt-1.5 text-sm font-semibold text-white leading-snug">{latest.match1} · {latest.match2}</div>
                            <div className="mt-2 flex items-center gap-3">
                                <span className="font-mono text-bn-cyan font-bold text-sm">@{latest.combined}</span>
                                <span className="font-mono text-[10px] px-2 py-0.5 rounded bg-bn-green/15 text-bn-green border border-bn-green/30 font-bold">WON</span>
                            </div>
                        </div>
                        {latestSingle && (
                            <div className="bg-[#0B0F17]/90 backdrop-blur-xl border border-bn-cyan/30 rounded-xl px-4 py-3.5" data-testid="hero-latest-single-card-mobile">
                                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-slate-500">
                                    <BadgeCheck className="w-3.5 h-3.5 text-bn-green" />
                                    LATEST SINGLE · {latestSingle.date}
                                </div>
                                <div className="mt-1.5 text-sm font-semibold text-white leading-snug">{latestSingle.teams}</div>
                                <div className="mt-2 flex items-center gap-3">
                                    <span className="font-mono text-bn-cyan font-bold text-sm">
                                        {latestSingle.pick} @{Number(latestSingle.odds).toFixed(2)}
                                    </span>
                                    <span className={`font-mono text-[10px] px-2 py-0.5 rounded border font-bold ${latestSingle.status === "lost" ? "bg-red-400/15 text-red-400 border-red-400/30" : "bg-bn-green/15 text-bn-green border-bn-green/30"}`}>
                                        {latestSingle.status === "lost" ? "LOST" : "WON"}
                                    </span>
                                </div>
                            </div>
                        )}
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="lg:col-span-5 hidden lg:block"
                    style={{ perspective: 1200 }}
                    onMouseMove={onMouseMove}
                    onMouseLeave={() => { mx.set(0); my.set(0); }}
                >
                    <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative">
                        <div className="absolute -inset-4 bg-bn-blue/25 blur-3xl rounded-full" />
                        <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_40px_80px_rgba(0,0,0,0.6)]">
                            <img src={HERO_IMG} alt="Football match action under stadium lights" className="w-full h-[460px] object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#05070A] via-transparent to-transparent" />
                        </div>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.2, duration: 0.8 }}
                            className="absolute -bottom-16 inset-x-0 flex flex-col xl:flex-row gap-4 items-center justify-center"
                        >
                            {latestSingle && (
                                <div data-testid="hero-latest-single-card" className="w-[218px] bg-[#0B0F17]/90 backdrop-blur-xl border border-bn-cyan/30 rounded-xl px-5 py-4 shadow-[0_20px_50px_rgba(0,0,0,0.5)] animate-float">
                                    <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-slate-500">
                                        <BadgeCheck className="w-3.5 h-3.5 text-bn-green" />
                                        LATEST SINGLE · {latestSingle.date}
                                    </div>
                                    <div className="mt-2 text-sm font-semibold text-white">{latestSingle.teams}</div>
                                    <div className="mt-2 flex items-center gap-3">
                                        <span className="font-mono text-bn-cyan font-bold">
                                            {latestSingle.pick} @{Number(latestSingle.odds).toFixed(2)}
                                        </span>
                                        <span className={`font-mono text-[10px] px-2 py-0.5 rounded border font-bold ${latestSingle.status === "lost" ? "bg-red-400/15 text-red-400 border-red-400/30" : "bg-bn-green/15 text-bn-green border-bn-green/30"}`}>
                                            {latestSingle.status === "lost" ? "LOST" : "WON"}
                                        </span>
                                    </div>
                                </div>
                            )}
                            <div data-testid="hero-latest-double-card" className="w-[218px] bg-[#0B0F17]/90 backdrop-blur-xl border border-bn-blue/30 rounded-xl px-5 py-4 shadow-[0_20px_50px_rgba(0,0,0,0.5)] animate-float">
                                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-slate-500">
                                    <BadgeCheck className="w-3.5 h-3.5 text-bn-green" />
                                    LATEST DOUBLE · {latest.date}
                                </div>
                                <div className="mt-2 text-sm font-semibold text-white">{latest.match1}</div>
                                <div className="text-sm font-semibold text-white">{latest.match2}</div>
                                <div className="mt-2 flex items-center gap-3">
                                    <span className="font-mono text-bn-cyan font-bold">@{latest.combined}</span>
                                    <span className={`font-mono text-[10px] px-2 py-0.5 rounded border font-bold ${latest.status === "lost" ? "bg-red-400/15 text-red-400 border-red-400/30" : "bg-bn-green/15 text-bn-green border-bn-green/30"}`}>{latest.status === "lost" ? "LOST" : "WON"}</span>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>

            <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6 }}
                onClick={() => scrollToSection(lenisRef.current, "records")}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500 hover:text-bn-blue transition-colors"
                aria-label="Scroll down"
                data-testid="hero-scroll-indicator"
            >
                <ChevronDown className="w-6 h-6 animate-bounce" />
            </motion.button>
        </section>
    );
};
